import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { formatDecimal, parseScaled } from '../ui/numbers';
import type { Invoice, InvoiceLine } from './types';
import { sameValues, useRowGuard, useUnsavedGuard } from '../ui/unsaved';

type Props = {
  invoice: Invoice;
  editable: boolean;
  onSaved: (inv: Invoice) => void;
  onError: (e: unknown) => void;
  /** The table's name for screen readers (two tables on a correction). */
  label?: string;
  /** Lines that may be changed but not removed (a correction's lines of the corrected invoice: set to 0 instead). */
  keptIds?: ReadonlySet<string>;
};

const quantityText = (milli: number) => formatDecimal(milli / 1000);

/** One line of a draft, edited in place: name, quantity, unit price (saved when the field is left). */
function EditableRow({ line, amounts, onSave, onRemove, busy, kept }: { line: InvoiceLine; amounts: boolean; onSave: (patch: Record<string, unknown>) => Promise<unknown>; onRemove: () => void; busy: boolean; kept: boolean }) {
  const opening = { name: line.name, qty: quantityText(line.quantityMilli), price: line.unitNetGrosze === undefined ? '' : plnToInput(line.unitNetGrosze) };
  const [name, setName] = useState(opening.name);
  const [qty, setQty] = useState(opening.qty);
  const [price, setPrice] = useState(opening.price);
  /** Leaving a cell saves what changed in the row (a wrong number goes back to the saved one, as before). */
  const row = useRowGuard(!sameValues({ name: name.trim(), qty, price }, opening), async () => {
    const patch: Record<string, unknown> = {};
    let ok = true;
    if (name.trim() !== '' && name.trim() !== line.name) patch.name = name.trim();
    else if (name.trim() === '') ok = false;
    const milli = parseScaled(qty, 3);
    if (milli === null || milli <= 0) {
      setQty(opening.qty);
      ok = false;
    } else if (milli !== line.quantityMilli) patch.quantityMilli = milli;
    if (price !== opening.price) {
      try {
        const grosze = parsePln(price);
        if (grosze !== line.unitNetGrosze) patch.unitNetGrosze = grosze;
      } catch {
        setPrice(opening.price);
        ok = false;
      }
    }
    if (Object.keys(patch).length > 0) await onSave(patch);
    return ok;
  });
  const save = () => void row.save();
  const saveName = save;
  const saveQty = save;
  const savePrice = save;
  const enter = (e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && e.currentTarget.blur();
  return (
    <tr ref={row.ref}>
      <td>
        <input className="inv-cell" aria-label={`Nazwa pozycji ${line.sort + 1}`} value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName} onKeyDown={enter} />
      </td>
      <td className="num">
        <input className="inv-cell num" inputMode="decimal" aria-label={`Ilość: ${line.name}`} value={qty} onChange={(e) => setQty(e.target.value)} onBlur={saveQty} onKeyDown={enter} />
      </td>
      <td className="wide-only">{line.unit}</td>
      {amounts && (
        <>
          <td className="num wide-only">
            <input className="inv-cell num" inputMode="decimal" aria-label={`Cena netto: ${line.name}`} value={price} onChange={(e) => setPrice(e.target.value)} onBlur={savePrice} onKeyDown={enter} />
          </td>
          <td className="num nowrap">{line.netGrosze === undefined ? '—' : formatPln(line.netGrosze)}</td>
        </>
      )}
      <td className="inv-remove">
        {!kept && (
          <button className="btn text small" type="button" aria-label={`Usuń pozycję: ${line.name}`} disabled={busy} onClick={onRemove}>
            ×
          </button>
        )}
      </td>
    </tr>
  );
}

/** The invoice's lines: read-only, or (a draft) edited in place with "Dodaj pozycję". */
export function InvoiceLines({ invoice, editable, onSaved, onError, label = 'Pozycje faktury', keptIds }: Props) {
  const amounts = invoice.lines.some((l) => l.netGrosze !== undefined) || (invoice.lines.length === 0 && invoice.netGrosze !== undefined);
  const save = useMutation({
    mutationFn: ({ line, patch }: { line: InvoiceLine; patch: Record<string, unknown> }) => api<Invoice>(`/api/invoices/lines/${line.id}`, { method: 'PATCH', body: { ...patch, version: line.version } }),
    onSuccess: onSaved,
    onError,
  });
  const remove = useMutation({
    mutationFn: (line: InvoiceLine) => api<Invoice>(`/api/invoices/lines/${line.id}?version=${line.version}`, { method: 'DELETE' }),
    onSuccess: onSaved,
    onError,
  });
  const [newName, setNewName] = useState('');
  const [newNet, setNewNet] = useState('');
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Invoice>(`/api/invoices/${invoice.id}/lines`, { method: 'POST', body: { ...body, version: invoice.version } }),
    onSuccess: (inv) => {
      setNewName('');
      setNewNet('');
      onSaved(inv);
    },
    onError,
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (newName.trim() === '') return;
    try {
      add.mutate({ name: newName.trim(), ...(amounts ? { netGrosze: newNet.trim() === '' ? 0 : parsePln(newNet) } : {}) });
    } catch (err) {
      onError(err);
    }
  }
  // owner 2026-10-23 21:14: a new line typed and not added: leaving asks first
  const addRef = useUnsavedGuard({
    dirty: editable && (newName.trim() !== '' || newNet.trim() !== ''),
    save: async () => {
      if (newName.trim() === '') return false;
      try {
        await add.mutateAsync({ name: newName.trim(), ...(amounts ? { netGrosze: newNet.trim() === '' ? 0 : parsePln(newNet) } : {}) });
        return true;
      } catch (err) {
        onError(err);
        return false;
      }
    },
  });
  return (
    <>
      <div className="table-wrap">
        <table className="table inv-lines" aria-label={label}>
          <thead>
            <tr>
              <th scope="col">Nazwa</th>
              <th scope="col" className="num">
                Ilość
              </th>
              <th scope="col" className="wide-only">
                J.m.
              </th>
              {amounts && (
                <>
                  <th scope="col" className="num wide-only">
                    Cena netto
                  </th>
                  <th scope="col" className="num">
                    Wartość netto
                  </th>
                </>
              )}
              {editable && <th scope="col" aria-label="Usuń" />}
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) =>
              editable ? (
                <EditableRow key={`${l.id}:${l.version}`} line={l} amounts={amounts} busy={remove.isPending} kept={keptIds?.has(l.id) ?? false} onSave={(patch) => save.mutateAsync({ line: l, patch })} onRemove={() => remove.mutate(l)} />
              ) : (
                <tr key={l.id}>
                  <td>{l.name}</td>
                  <td className="num">{quantityText(l.quantityMilli)}</td>
                  <td className="wide-only">{l.unit}</td>
                  {amounts && (
                    <>
                      <td className="num nowrap wide-only">{l.unitNetGrosze === undefined ? '—' : formatPln(l.unitNetGrosze)}</td>
                      <td className="num nowrap">{l.netGrosze === undefined ? '—' : formatPln(l.netGrosze)}</td>
                    </>
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {editable && (
        <form ref={addRef} className="inv-add" aria-label="Dodaj pozycję" onSubmit={submit}>
          <input aria-label="Nazwa nowej pozycji" placeholder="Nowa pozycja" value={newName} onChange={(e) => setNewName(e.target.value)} />
          {amounts && <input className="num" inputMode="decimal" aria-label="Kwota netto nowej pozycji" placeholder="0,00 zł" value={newNet} onChange={(e) => setNewNet(e.target.value)} />}
          <button className="btn small" type="submit" disabled={add.isPending || newName.trim() === ''}>
            Dodaj pozycję
          </button>
        </form>
      )}
    </>
  );
}
